import { useQuery } from "@tanstack/react-query";
import { kanban } from "../../lib/api";
import { useUI } from "../../lib/store";
import { Chip, StatusRow } from "../shared/Card";
import { STATUS_VAR, statusLabel } from "../shared/StatusDot";
import { formatCost } from "../../lib/format";
import type { Task, TaskStatus } from "../../lib/types";

// Operator-facing order — most-actionable / most-recent first.
const GROUP_ORDER: TaskStatus[] = [
  "done",
  "in_review",
  "in_progress",
  "needs_input",
  "todo",
  "backlog",
  "interrupted",
];

/**
 * Conductor-style left panel: the focused project's tasks grouped by status.
 * Shares the exact `["kanban", …]` query cache with the board (no extra
 * fetch). Clicking a row selects the task and opens its Activity, mirroring
 * the board card's open behaviour. No diff stats — status, title, branch,
 * agent, cost.
 */
export function TaskListPanel() {
  const projectId = useUI((s) => s.selectedProjectId);
  const selectedTaskId = useUI((s) => s.selectedTaskId);
  const selectTask = useUI((s) => s.setSelectedTaskId);
  const setMode = useUI((s) => s.setRightPaneMode);
  const revealPane = useUI((s) => s.setRightPaneVisible);
  const setDrawer = useUI((s) => s.setRightPaneOpenMobile);

  const { data } = useQuery({
    queryKey: ["kanban", projectId ?? "all"],
    queryFn: () => kanban.board(projectId ?? undefined),
    refetchInterval: 5_000,
    enabled: projectId != null,
  });

  if (projectId == null) {
    return (
      <div className="px-4 py-10 text-center text-small text-text-tertiary">
        Select a project to see its tasks by status.
      </div>
    );
  }

  const byStatus = new Map<TaskStatus, Task[]>();
  for (const col of data?.columns ?? []) byStatus.set(col.status, col.tasks);
  const groups = GROUP_ORDER.map((s) => ({ status: s, tasks: byStatus.get(s) ?? [] })).filter(
    (g) => g.tasks.length > 0,
  );

  const open = (id: string) => {
    selectTask(id);
    setMode("activity");
    revealPane(true);
    setDrawer(true);
  };

  if (groups.length === 0) {
    return (
      <div className="px-4 py-10 text-center text-small text-text-tertiary">
        No tasks yet in this project.
      </div>
    );
  }

  return (
    <div className="px-2 py-2 space-y-4">
      {groups.map((g) => (
        <div key={g.status}>
          <div className="flex items-center gap-2 px-1.5 mb-1.5">
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ background: STATUS_VAR[g.status] }}
            />
            <span className="text-caption text-text-tertiary">
              {statusLabel(g.status)}
            </span>
            <span className="text-caption text-text-quaternary">
              {g.tasks.length}
            </span>
          </div>
          <div className="space-y-1.5">
            {g.tasks.map((t) => (
              <StatusRow
                key={t.id}
                dotColor={STATUS_VAR[t.status]}
                pulse={t.status === "in_progress"}
                selected={t.id === selectedTaskId}
                onClick={() => open(t.id)}
                ariaLabel={`${t.title} — ${statusLabel(t.status)}`}
                trailing={
                  Number(t.cost_usd) > 0 ? (
                    <span className="text-mono text-text-tertiary">
                      {formatCost(t.cost_usd)}
                    </span>
                  ) : undefined
                }
              >
                <div className="text-small text-text-primary truncate">
                  {t.title}
                </div>
                {(t.worktree_name || t.agent_type) && (
                  <div className="mt-1 flex items-center gap-1.5 min-w-0">
                    {t.worktree_name && (
                      <Chip title={t.worktree_name}>{t.worktree_name}</Chip>
                    )}
                    {t.agent_type && <Chip>{t.agent_type}</Chip>}
                  </div>
                )}
              </StatusRow>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
